import React, { useState, useEffect } from 'react';

function NoteReview({ notes, noteStatus, onSave, onApprove, onReject, saving, onPushToEHR, ehrPushStatus, ehrPushError }) {
  const [sections, setSections] = useState([]);
  const [hasEdits, setHasEdits] = useState(false);

  useEffect(() => {
    if (notes && Array.isArray(notes)) {
      setSections(notes.map(s => ({ ...s })));
      setHasEdits(false);
    } else if (notes && typeof notes === 'object') {
      // Convert object format to array of {title, content}
      const arr = Object.entries(notes).map(([title, content]) => ({
        title,
        content: typeof content === 'string' ? content : JSON.stringify(content, null, 2)
      }));
      setSections(arr);
      setHasEdits(false);
    }
  }, [notes]);

  const handleSectionChange = (idx, value) => {
    const updated = [...sections];
    updated[idx] = { ...updated[idx], content: value };
    setSections(updated);
    setHasEdits(true);
  };

  const handleSave = () => {
    if (onSave) onSave(sections);
    setHasEdits(false);
  };

  const isEditable = ['generated', 'reviewed', 'rejected'].includes(noteStatus);
  const canApprove = ['generated', 'reviewed'].includes(noteStatus);
  const isApproved = noteStatus === 'approved';

  if (!notes) {
    return (
      <div style={styles.empty}>
        <NoteIcon />
        <p style={styles.emptyTitle}>No Notes Yet</p>
        <p style={styles.emptyText}>
          Clinical notes will be generated after the session ends and HealthScribe finishes processing.
        </p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Status Bar */}
      <div style={styles.statusBar}>
        <StatusBadge status={noteStatus} />
        <div style={styles.statusActions}>
          {isEditable && hasEdits && (
            <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Edits'}
            </button>
          )}
          {canApprove && (
            <>
              <button style={styles.approveBtn} onClick={onApprove}>
                <CheckIcon /> Approve
              </button>
              <button style={styles.rejectBtn} onClick={onReject}>
                Reject
              </button>
            </>
          )}
        </div>
      </div>

      {isApproved && ehrPushStatus !== 'pushed' && (
        <div style={styles.approvedBanner}>
          <CheckIcon /> Notes approved and locked
        </div>
      )}

      {/* EHR Push Status */}
      {ehrPushStatus === 'pushed' && (
        <div style={styles.pushedBanner}>
          <CheckIcon /> Notes pushed to EHR
        </div>
      )}

      {ehrPushStatus === 'failed' && ehrPushError && (
        <div style={styles.pushErrorBanner}>
          Push failed: {ehrPushError}
        </div>
      )}

      {/* Push to EHR button — visible when approved but not yet pushed */}
      {isApproved && onPushToEHR && ehrPushStatus !== 'pushed' && (
        <div style={styles.pushSection}>
          <button
            style={ehrPushStatus === 'failed' ? styles.retryPushBtn : styles.pushBtn}
            onClick={onPushToEHR}
            disabled={ehrPushStatus === 'pushing'}
          >
            {ehrPushStatus === 'pushing' ? 'Pushing...' : ehrPushStatus === 'failed' ? 'Retry Push to EHR' : 'Push to EHR'}
          </button>
        </div>
      )}

      {/* Note Sections */}
      <div style={styles.sectionsContainer}>
        {sections.map((section, idx) => (
          <div key={idx} style={styles.section}>
            <label style={styles.sectionLabel}>
              {formatSectionTitle(section.title)}
            </label>
            {isEditable ? (
              <textarea
                style={styles.sectionTextarea}
                value={section.content || ''}
                onChange={(e) => handleSectionChange(idx, e.target.value)}
                rows={Math.max(3, (section.content || '').split('\n').length + 1)}
              />
            ) : (
              <div style={styles.sectionContent}>
                {section.content || '(empty)'}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function formatSectionTitle(title) {
  if (!title) return '';
  return title
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function StatusBadge({ status }) {
  const colors = {
    pending: { bg: '#fef3c7', color: '#92400e', label: 'Pending' },
    generated: { bg: '#dbeafe', color: '#1e40af', label: 'Generated' },
    reviewed: { bg: '#e0e7ff', color: '#3730a3', label: 'Reviewed' },
    approved: { bg: '#d1fae5', color: '#065f46', label: 'Approved' },
    rejected: { bg: '#fee2e2', color: '#991b1b', label: 'Rejected' },
    pushed_to_ehr: { bg: '#d1fae5', color: '#065f46', label: 'Pushed to EHR' }
  };
  const c = colors[status] || colors.pending;
  return (
    <span style={{
      ...styles.badge,
      backgroundColor: c.bg,
      color: c.color
    }}>
      {c.label}
    </span>
  );
}

function NoteIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ verticalAlign: 'middle' }}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '16px'
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '60px 20px',
    color: '#9ca3af'
  },
  emptyTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
    color: '#6b7280'
  },
  emptyText: {
    margin: 0,
    fontSize: '14px',
    textAlign: 'center',
    maxWidth: '360px'
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e5e7eb'
  },
  statusActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  },
  badge: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600
  },
  saveBtn: {
    padding: '6px 16px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    backgroundColor: '#fff',
    color: '#374151',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer'
  },
  approveBtn: {
    padding: '6px 16px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#059669',
    color: '#fff',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  rejectBtn: {
    padding: '6px 16px',
    borderRadius: '6px',
    border: '1px solid #fca5a5',
    backgroundColor: '#fff',
    color: '#dc2626',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer'
  },
  approvedBanner: {
    padding: '10px 16px',
    backgroundColor: '#d1fae5',
    color: '#065f46',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  pushedBanner: {
    padding: '10px 16px',
    backgroundColor: '#d1fae5',
    color: '#065f46',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  pushErrorBanner: {
    padding: '10px 16px',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 500
  },
  pushSection: {
    display: 'flex',
    justifyContent: 'center'
  },
  pushBtn: {
    padding: '10px 24px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#2563eb',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  retryPushBtn: {
    padding: '10px 24px',
    borderRadius: '8px',
    border: '1px solid #fca5a5',
    backgroundColor: '#fff',
    color: '#dc2626',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer'
  },
  sectionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  sectionLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
    textTransform: 'capitalize'
  },
  sectionTextarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    lineHeight: '1.6',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '60px',
    color: '#1f2937',
    boxSizing: 'border-box'
  },
  sectionContent: {
    padding: '10px 12px',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#1f2937',
    whiteSpace: 'pre-wrap'
  }
};

export default NoteReview;
